import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Link,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { OpenInNew } from '@mui/icons-material';
import { createDeploymentForMain } from '../../firebase';
import { useMainCommits } from '../../contexts/MainCommitsContext';
import { useDeploymentStatus } from '../../contexts/DeploymentStatusContext';
import { useConfig } from '../../contexts/ConfigContext';
import { useConfiguredEnvironments, useVisibleEnvironments } from '../../contexts/EnvironmentsContext';
import { usePools } from '../../contexts/PoolsContext';
import QueuedDeploysList from '../../components/QueuedDeploysList';
import { useEnvironments, useEnvironment } from '../../hooks/useEnvironments';
import { formatDate } from '../../utils/deploymentUtils';
import DeploymentOptionsDialog from '../../components/DeploymentOptionsDialog';
import { isLeaseActive, type RolloutSetupOptions } from '@finvari/deliver';

const MainBranch: React.FC = () => {
  const navigate = useNavigate();
  const { mainCommits, loading, error } = useMainCommits();
  const { liveDeploymentsByEnvironment, getPRDeploymentStatus } = useDeploymentStatus();
  const { environments } = useEnvironments();
  const { config, userProfile } = useConfig();
  const { environmentsById, loading: environmentsLoading } = useConfiguredEnvironments();
  // Deploy-target picker hides personal environments owned by others, and pooled environments
  // (you target the pool instead). Pools and any environment the user currently holds are added below.
  const environmentDocs = useVisibleEnvironments();
  const { pools, queuedRollouts } = usePools();
  const heldEnvironment = useMemo(
    () =>
      Object.values(environmentsById).find(
        (e) => e.poolId && isLeaseActive(e.lease) && e.lease?.holderUid === userProfile?.uid
      ),
    [environmentsById, userProfile?.uid]
  );
  const [deployingMain, setDeployingMain] = useState<boolean>(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deploySuccess, setDeploySuccess] = useState<string | null>(null);
  // Target value: an environment's gcpProjectId, or `pool:<poolId>` for a pool.
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const selectedPoolId = selectedTarget.startsWith('pool:') ? selectedTarget.slice(5) : undefined;
  const selectedEnvironment = selectedPoolId ? '' : selectedTarget;
  const [dialogOpen, setDialogOpen] = useState(false);

  // Check if target environment has active operations
  const { hasAnyOperationInFlight } = useEnvironment(selectedEnvironment);

  // Set default target when config loads, preferring the user's default environment.
  useEffect(() => {
    if (!selectedTarget) {
      const defaultId = userProfile?.defaultEnvironmentId;
      const resolvedId =
        defaultId && environmentsById[defaultId] && !environmentsById[defaultId].poolId
          ? defaultId
          : environmentDocs[0]?.gcpProjectId ?? (pools[0] ? `pool:${pools[0].poolId}` : '');
      if (resolvedId) {
        setSelectedTarget(resolvedId);
      }
    }
  }, [environmentDocs, environmentsById, pools, userProfile, selectedTarget]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleDeployMain = async (setupOptions?: RolloutSetupOptions, selectedModules?: string[], autoDeployOnPush?: boolean) => {
    setDeployingMain(true);
    setDeployError(null);
    setDeploySuccess(null);
    try {
      if (selectedPoolId) {
        const res: any = await createDeploymentForMain('', selectedModules, setupOptions, selectedPoolId, autoDeployOnPush);
        const data = res?.data ?? {};
        if (data.environment) {
          navigate(`/environment/${data.environment}`);
        } else {
          setDeploySuccess(data.message || 'Deploy queued — waiting for a free environment in the pool.');
        }
      } else {
        await createDeploymentForMain(selectedEnvironment, selectedModules, setupOptions, undefined, autoDeployOnPush);

        // Navigate to the environment page to show deployment progress
        navigate(`/environment/${selectedEnvironment}`);
      }
    } catch (err: any) {
      setDeployError(err.message || 'Failed to start deployments for main branch');
    } finally {
      setDeployingMain(false);
    }
  };


  // Function to get environment display names for environment IDs
  const getEnvironmentDisplayName = (environmentId: string): string => {
    const environment = environments.find(env => env.environmentId === environmentId);
    return environment ? environment.environmentName : environmentId;
  };

  // Function to get environments that have a specific commit deployed
  const getEnvironmentsForCommit = (commitHash: string): Array<{id: string, name: string}> => {
    const environmentsWithCommit: Array<{id: string, name: string}> = [];
    
    liveDeploymentsByEnvironment.forEach((deployment, environmentId) => {
      // Check if any of the deployments in this environment have this commit hash
      if (deployment.deployments) {
        const hasCommit = Array.from(deployment.deployments.values()).some(dep => 
          dep.commitHash === commitHash
        );
        if (hasCommit) {
          environmentsWithCommit.push({
            id: environmentId,
            name: getEnvironmentDisplayName(environmentId)
          });
        }
      }
    });
    
    // Sort environments by name to ensure consistent rendering order
    return environmentsWithCommit.sort((a, b) => a.name.localeCompare(b.name));
  };



  // DataGrid columns configuration
  const columns: GridColDef[] = [
    {
      field: 'hash',
      headerName: 'Commit',
      width: 120,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
          {params.value.substring(0, 8)}
        </Typography>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 300,
      display: 'flex',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" noWrap>
            {params.value.split('\n')[0]} {/* First line of commit message */}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            by {params.row.author}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'githubLink',
      headerName: 'GitHub',
      width: 100,
      sortable: false,
      display: 'flex',
      renderCell: (params) => (
        <Link
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <OpenInNew fontSize="small" />
        </Link>
      ),
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: 'environments',
      headerName: 'Deployed Environments',
      flex: 2,
      minWidth: 250,
      sortable: false,
      display: 'flex',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {params.value.length > 0 ? (
            params.value.map((env: {id: string, name: string}) => (
              <Chip
                key={env.id}
                label={env.name}
                size="small"
                color="primary"
                variant="outlined"
                title={`Environment ID: ${env.id}`}
              />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              Not deployed
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'isLatest',
      headerName: 'Status',
      width: 100,
      display: 'flex',
      renderCell: (params) => (
        params.value ? (
          <Chip
            label="Latest"
            size="small"
            color="success"
            variant="filled"
          />
        ) : null
      ),
    },
  ];

  // Get main deployment status
  const mainDeploymentStatus = getPRDeploymentStatus(0);

  // Transform data for DataGrid
  const rows = mainCommits.map((commit, index) => {
    const environmentsWithCommit = getEnvironmentsForCommit(commit.hash);
    const isLatest = index === 0; // First commit is the latest
    
    return {
      id: commit.hash,
      hash: commit.hash,
      message: commit.message,
      author: commit.author,
      githubLink: commit.url,
      date: commit.date,
      environments: environmentsWithCommit,
      isLatest,
    };
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Success/Error Messages */}
      {deploySuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setDeploySuccess(null)}>
          {deploySuccess}
        </Alert>
      )}
      {deployError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDeployError(null)}>
          {deployError}
        </Alert>
      )}

      {/* Main Branch Deployment Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🚀 Main Branch Deployment
          </Typography>
          
          {/* Main Deployment Status */}
          {mainDeploymentStatus && (
            <Box sx={{ mb: 2 }}>
              {!mainDeploymentStatus.isCurrentlyDeployed ? (
                <Alert severity="info">
                  Not Currently Deployed
                  {mainDeploymentStatus.moduleCount > 0 && (
                    <Typography variant="body2">
                      Has {mainDeploymentStatus.moduleCount} module deployment(s) but not currently live
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={mainDeploymentStatus.overallStatus.replace('_', ' ')}
                    color={
                      mainDeploymentStatus.overallStatus === 'success' ? 'success' :
                      mainDeploymentStatus.overallStatus === 'failed' ? 'error' : 'warning'
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    Deployed on: {mainDeploymentStatus.environments.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Main Deployment Controls */}
          {hasAnyOperationInFlight && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Environment has active operations. Wait for them to complete before deploying.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Deploy target</InputLabel>
              <Select
                value={selectedTarget}
                onChange={(e) => setSelectedTarget(e.target.value)}
                disabled={deployingMain}
                label="Deploy target"
              >
                {environmentsLoading && <MenuItem disabled>Loading environments...</MenuItem>}
                {!environmentsLoading && environmentDocs.length === 0 && pools.length === 0 && !heldEnvironment && (
                  <MenuItem disabled>No deploy targets available</MenuItem>
                )}
                {environmentDocs.map((env) => (
                  <MenuItem key={env.gcpProjectId} value={env.gcpProjectId}>
                    {env.name} ({env.gcpProjectId})
                  </MenuItem>
                ))}
                {heldEnvironment && (
                  <MenuItem value={heldEnvironment.gcpProjectId}>
                    {heldEnvironment.name} (you hold this lease)
                  </MenuItem>
                )}
                {pools.map((pool) => (
                  <MenuItem key={pool.poolId} value={`pool:${pool.poolId}`}>
                    Pool: {pool.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleOpenDialog}
              disabled={deployingMain || !selectedTarget || (!selectedPoolId && hasAnyOperationInFlight)}
            >
              {deployingMain ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Deploying Main...
                </>
              ) : (
                'Deploy Main Branch'
              )}
            </Button>
          </Box>

          {(() => {
            const mainQueued = queuedRollouts.filter((r) => r.prNumber === 0);
            return mainQueued.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Queued pool deploys
                </Typography>
                <QueuedDeploysList rollouts={mainQueued} showPool />
              </Box>
            ) : null;
          })()}
        </CardContent>
      </Card>

      <DeploymentOptionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        environmentId={selectedEnvironment}
        poolId={selectedPoolId}
        environmentName={
          selectedPoolId
            ? `Pool: ${pools.find((p) => p.poolId === selectedPoolId)?.name ?? selectedPoolId}`
            : environmentsById[selectedEnvironment]?.name ?? selectedEnvironment
        }
        availableModules={config?.modules}
        onDeployOnly={(selectedModules, autoDeployOnPush) => handleDeployMain(undefined, selectedModules, autoDeployOnPush)}
        onDeployAndPrep={(options, selectedModules, autoDeployOnPush) => handleDeployMain(options, selectedModules, autoDeployOnPush)}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Main Branch Deployments
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Recent commits to the main branch and their deployment status across environments
          </Typography>
        </Box>
        

      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}



      {/* Main Commits Table */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default MainBranch;